/**
 * ExtraServicesEditor — hauler tenant Extra Services admin (OPEN-111).
 *
 * Create / edit / deactivate the tenant service-charge catalog (`service_charges`):
 * the extra charges a driver adds to a ticket (e.g. washout, standby hours). Until
 * now the catalog had no web screen. Modeled on ProductsEditor.
 *
 * Writes go to /v1/service-charges (MANAGE_SERVICE_CHARGES — TENANT_ADMIN only).
 * There is no DELETE: deactivate is a SOFT delete (PUT `{ isActive: false }`) since
 * historical tickets reference a charge. `code` is immutable after creation, and
 * the PUT schema is strict, so an edit sends only the fields that changed.
 *
 * Rate is typed in dollars and stored as integer cents via the shared exact
 * parser — never float math. More than two decimals is refused rather than rounded.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import {
  formatCentsAsDollars,
  hasSubCentPrecision,
  parseDollarsToCents,
} from '@lodex-pro/shared/money';
import { fieldStyle, FormRow } from '../sites-operators/form-bits';
import {
  useServiceChargesAdmin,
  SERVICE_CHARGES_ADMIN_QUERY_KEY,
  SERVICE_CHARGE_UNITS,
  type AdminServiceCharge,
  type ServiceChargeStage,
} from './use-service-charges-admin';
import { formatNumber } from '@lodex-pro/shared/number-format';

const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  marginBottom: '6px',
};
const btn: React.CSSProperties = {
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  background: 'var(--color-accent-blue)',
  borderColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const stageTag: React.CSSProperties = {
  fontSize: '11px',
  padding: '1px 6px',
  borderRadius: '4px',
  border: '1px solid var(--color-border-primary)',
  color: 'var(--color-text-secondary)',
};

const STAGE_OPTIONS: ReadonlyArray<{ value: ServiceChargeStage; label: string }> = [
  { value: 'GAUGING', label: 'Gauging screen (default)' },
  { value: 'DISPOSAL', label: 'Disposal screen (only known at or after unloading)' },
];

/** A row without a stage (API before PR #677) is a gauging charge — the DB default. */
function stageOf(s: AdminServiceCharge): ServiceChargeStage {
  return s.stage === 'DISPOSAL' ? 'DISPOSAL' : 'GAUGING';
}

interface FormState {
  code: string;
  label: string;
  unitOfMeasure: string;
  /** Dollars as typed, e.g. "12.50". Parsed to cents only on save. */
  rate: string;
  sortOrder: string;
  stage: ServiceChargeStage;
}
const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  unitOfMeasure: 'EACH',
  rate: '',
  sortOrder: '0',
  stage: 'GAUGING',
};

const CODE_RE = /^[A-Z0-9_]{1,64}$/;
const INT_RE = /^-?\d+$/;

/** Dollars → integer cents, exactly; at most two decimals, never negative. Blank is $0. */
function parseRate(raw: string): number | null {
  if (raw.trim() === '') return 0;
  if (hasSubCentPrecision(raw)) return null;
  const parsed = parseDollarsToCents(raw);
  return parsed.ok && parsed.cents >= 0 ? parsed.cents : null;
}

export function ExtraServicesEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const { data, isLoading } = useServiceChargesAdmin(opts);
  const charges = data?.serviceCharges ?? [];

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<AdminServiceCharge | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const invalidate = React.useCallback(async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: SERVICE_CHARGES_ADMIN_QUERY_KEY });
  }, [qc]);

  const startCreate = (): void => {
    setForm(EMPTY_FORM);
    setCreating(true);
    setEditing(null);
    setError(null);
  };
  const startEdit = (s: AdminServiceCharge): void => {
    setForm({
      code: s.code,
      label: s.label,
      unitOfMeasure: s.unitOfMeasure,
      rate: formatCentsAsDollars(s.defaultRateCents),
      sortOrder: String(s.sortOrder),
      stage: stageOf(s),
    });
    setEditing(s);
    setCreating(false);
    setError(null);
  };
  const cancel = (): void => {
    setCreating(false);
    setEditing(null);
    setError(null);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = async (): Promise<void> => {
    const code = form.code.trim().toUpperCase();
    const label = form.label.trim();
    const sortRaw = form.sortOrder.trim();
    if (label === '' || label.length > 120) {
      setError('Label is required (up to 120 characters).');
      return;
    }
    // `code` is only validated / sent on create — it is immutable on edit.
    if (editing === null && !CODE_RE.test(code)) {
      setError('Code must be 1–64 chars: A–Z, 0–9, underscore.');
      return;
    }
    const defaultRateCents = parseRate(form.rate);
    if (defaultRateCents === null) {
      setError('Rate must be a dollar amount with at most 2 decimals (e.g. 12.50).');
      return;
    }
    if (sortRaw !== '' && !INT_RE.test(sortRaw)) {
      setError('Sort order must be a whole number.');
      return;
    }
    const sortOrder = sortRaw === '' ? 0 : Number(sortRaw);

    let body: Record<string, string | number>;
    if (editing === null) {
      body = {
        code,
        label,
        unitOfMeasure: form.unitOfMeasure,
        defaultRateCents,
        sortOrder,
        stage: form.stage,
      };
    } else {
      // PUT is strict and rejects an empty body — send only what changed.
      body = {};
      if (label !== editing.label) body.label = label;
      if (form.unitOfMeasure !== editing.unitOfMeasure) body.unitOfMeasure = form.unitOfMeasure;
      if (defaultRateCents !== editing.defaultRateCents) body.defaultRateCents = defaultRateCents;
      if (sortOrder !== editing.sortOrder) body.sortOrder = sortOrder;
      if (form.stage !== stageOf(editing)) body.stage = form.stage;
      if (Object.keys(body).length === 0) {
        cancel();
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const isEdit = editing !== null;
      const path = isEdit ? `/v1/service-charges/${editing.id}` : '/v1/service-charges';
      const res = await authedFetch(opts, path, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setError(
          res.status === 400 || res.status === 409
            ? 'Check your inputs — an extra service with this code may already exist.'
            : `Save failed (${String(res.status)}).`,
        );
        return;
      }
      await invalidate();
      toast.success(isEdit ? 'Extra service saved' : 'Extra service created');
      cancel();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (s: AdminServiceCharge, isActive: boolean): Promise<void> => {
    try {
      const res = await authedFetch(opts, `/v1/service-charges/${s.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) {
        toast.error('Could not update. Please try again.');
        return;
      }
      await invalidate();
      toast.success(isActive ? 'Extra service reactivated' : 'Extra service deactivated');
    } catch {
      toast.error('Network error — please try again.');
    }
  };

  const activeCount = charges.filter((s) => s.isActive).length;
  const formOpen = creating || editing !== null;

  return (
    <div style={{ maxWidth: '560px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={subText}>
          {formatNumber(activeCount)} active extra service{activeCount === 1 ? '' : 's'}
        </div>
        {!formOpen && (
          <button type="button" style={primaryBtn} onClick={startCreate}>
            + New extra service
          </button>
        )}
      </div>

      {formOpen && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '12px',
            marginTop: '10px',
            borderRadius: 'var(--radius-card)',
            background: 'var(--color-bg-secondary)',
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {editing !== null ? 'Edit extra service' : 'New extra service'}
          </div>

          <FormRow label="Code" htmlFor="svc-code" required>
            <>
              <input
                id="svc-code"
                value={form.code}
                disabled={editing !== null}
                placeholder="WASHOUT"
                onChange={(e): void => set('code', e.target.value.toUpperCase())}
                style={fieldStyle}
              />
              <div style={subText}>
                {editing !== null
                  ? 'Code is fixed after creation.'
                  : 'Stable machine identifier — 1–64 chars: A–Z, 0–9, underscore.'}
              </div>
            </>
          </FormRow>

          <FormRow label="Label" htmlFor="svc-label" required>
            <input
              id="svc-label"
              value={form.label}
              placeholder="Truck washout"
              onChange={(e): void => set('label', e.target.value)}
              style={fieldStyle}
            />
          </FormRow>

          <FormRow label="Unit" htmlFor="svc-unit" required>
            <select
              id="svc-unit"
              aria-label="Unit"
              value={form.unitOfMeasure}
              onChange={(e): void => set('unitOfMeasure', e.target.value)}
              style={fieldStyle}
            >
              {SERVICE_CHARGE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </FormRow>

          <FormRow label="Rate ($ per unit)" htmlFor="svc-rate">
            <>
              <input
                id="svc-rate"
                inputMode="decimal"
                value={form.rate}
                placeholder="0.00"
                onChange={(e): void => set('rate', e.target.value)}
                style={fieldStyle}
              />
              <div style={subText}>Default rate in dollars, up to 2 decimals.</div>
            </>
          </FormRow>

          <FormRow label="Sort order" htmlFor="svc-sort">
            <input
              id="svc-sort"
              inputMode="numeric"
              value={form.sortOrder}
              placeholder="0"
              onChange={(e): void => set('sortOrder', e.target.value)}
              style={fieldStyle}
            />
          </FormRow>

          <FormRow label="Entered on" htmlFor="svc-stage" required>
            <select
              id="svc-stage"
              aria-label="Entered on"
              value={form.stage}
              onChange={(e): void =>
                set('stage', e.target.value === 'DISPOSAL' ? 'DISPOSAL' : 'GAUGING')
              }
              style={fieldStyle}
            >
              {STAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormRow>

          {error !== null && (
            <div style={{ fontSize: '13px', color: 'var(--color-danger)' }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button
              type="button"
              style={primaryBtn}
              disabled={saving}
              onClick={(): void => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" style={btn} disabled={saving} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        {isLoading && <div style={subText}>Loading…</div>}
        {!isLoading && charges.length === 0 && (
          <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 600 }}>No extra services yet</span>
            <span style={subText}>
              Add the extra charges your drivers can put on a ticket (e.g. washout, standby hours).
            </span>
          </div>
        )}
        {charges.map((s) => (
          <div key={s.id} style={{ ...rowStyle, opacity: s.isActive ? 1 : 0.55 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{s.label}</span>{' '}
              <span style={subText}>
                · {s.code} · ${formatCentsAsDollars(s.defaultRateCents)} / {s.unitOfMeasure}
              </span>{' '}
              <span style={stageTag}>{stageOf(s) === 'DISPOSAL' ? 'Disposal' : 'Gauging'}</span>
              {!s.isActive && <span style={subText}> · inactive</span>}
            </div>
            <button type="button" style={btn} onClick={(): void => startEdit(s)}>
              Edit
            </button>
            <button type="button" style={btn} onClick={(): void => void setActive(s, !s.isActive)}>
              {s.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
